import { useDataPreparationOverlayElements } from "@/data-preparation-tool/data-preparation-overlay-context";
import { selectRevisionEntityWorldPositions } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { selectRevisionEntityDescendantScans } from "@/data-preparation-tool/store/revision/revision-scan-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { isClusterEntity } from "@faro-lotv/data-management";
import { FaroMenuItem, FullScreenIcon } from "@faro-lotv/flat-ui";
import { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import { JSX, useCallback } from "react";
import { Box3 } from "three";

interface FocusOnContextMenuActionItemProps {
  entity: CaptureTreeEntityRevision;
  disabled?: boolean;
  onFocus(): void;
}

/**
 * @returns context menu item for on focus action
 */
export function FocusOnContextMenuActionItem({
  entity,
  onFocus,
  disabled,
}: FocusOnContextMenuActionItemProps): JSX.Element {
  const { getState } = useAppStore();
  const { centerCameraEvent } = useDataPreparationOverlayElements();
  const descendantScans = useAppSelector(
    selectRevisionEntityDescendantScans(entity.id),
  );

  const focusOnSelection = useCallback(() => {
    const selectedEntities = isClusterEntity(entity)
      ? descendantScans.map((scan) => scan.id)
      : [entity.id];
    const state = getState();
    const scanPositions =
      selectRevisionEntityWorldPositions(selectedEntities)(state);
    const bbox = new Box3()
      .setFromPoints(scanPositions)
      // Add a padding around the bounding box
      // Ensures that all important scan features are visible
      // Also fixes camera positioning bugs when the bounding box has 0 volume
      .expandByScalar(3);

    centerCameraEvent.emit({ bbox });
    onFocus();
  }, [descendantScans, centerCameraEvent, entity, getState, onFocus]);

  return (
    <FaroMenuItem
      key="focus"
      label="Focus on"
      Icon={FullScreenIcon}
      onClick={focusOnSelection}
      disabled={(disabled ?? false) || descendantScans.length === 0}
    />
  );
}
